// @ts-nocheck
import React, { useState, useMemo } from "react";
import { GlobalAdmin } from "coolremote-sdk";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BugReportIcon from "@mui/icons-material/BugReport";
import useStyles from "../DaikinConsistency.style";
import { fmtDate } from "./shared";

const STATUS_OPTIONS = ["updated", "created", "reconnected", "disconnected"];

function buildParams(filters) {
  const params: Record<string, string> = {};
  if (filters.lineType) {
    params.lineType = filters.lineType;
  }
  if (filters.customer) {
    params.customer = filters.customer;
  }
  if (filters.device) {
    params.device = filters.device;
  }
  if (filters.line) {
    params.line = filters.line;
  }
  if (filters.address) {
    params.address = filters.address;
  }
  if (filters.fromDate) {
    params.fromDate = filters.fromDate;
  }
  if (filters.toDate) {
    params.toDate = filters.toDate;
  }
  if (filters.limitRows) {
    params.limitRows = filters.limitRows;
  }
  if (filters.statuses.length) {
    params.statuses = filters.statuses.join(",");
  }
  return params;
}

// customer -> device -> line -> rows, so the table can render collapsible groups instead of
// repeating Customer/Device/Line as columns on every single row.
function groupRows(rows) {
  const byCustomer = new Map();
  for (const row of rows) {
    if (!byCustomer.has(row.customerKey)) {
      byCustomer.set(row.customerKey, { label: row.customerLabel, devices: new Map() });
    }
    const devices = byCustomer.get(row.customerKey).devices;
    if (!devices.has(row.deviceSerial)) {
      devices.set(row.deviceSerial, new Map());
    }
    const lines = devices.get(row.deviceSerial);
    if (!lines.has(row.line)) {
      lines.set(row.line, { line: row.line, lineType: row.lineType, rows: [] });
    }
    lines.get(row.line).rows.push(row);
  }
  return byCustomer;
}

export default function UnitMappingSnapshotsTab() {
  const classes = useStyles();

  const [filters, setFilters] = useState({
    lineType: "service",
    customer: "",
    device: "",
    line: "",
    address: "",
    fromDate: "",
    toDate: "",
    limitRows: "20",
    statuses: ["updated"],
  });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collapsedCustomers, setCollapsedCustomers] = useState(new Set());

  const groupedRows = useMemo(() => groupRows(rows), [rows]);

  const setFilter = (key) => (event) => setFilters((prev) => ({ ...prev, [key]: event.target.value }));

  const toggleStatus = (status) => (event) => {
    setFilters((prev) => ({
      ...prev,
      statuses: event.target.checked ? [...prev.statuses, status] : prev.statuses.filter((s) => s !== status),
    }));
  };

  const toggleCustomer = (customerKey) => {
    setCollapsedCustomers((prev) => {
      const next = new Set(prev);
      next.has(customerKey) ? next.delete(customerKey) : next.add(customerKey);
      return next;
    });
  };

  const runQuery = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await GlobalAdmin.getUnitMappingSnapshots(buildParams(filters));
      const data = response?.data || response;
      setRows(data?.rows || []);
      setSummary({ total: data?.total || 0, customerCount: data?.customerCount || 0, deviceCount: data?.deviceCount || 0 });
      setCollapsedCustomers(new Set());
    } catch (err) {
      setError(err?.message || "Failed to load unit mapping snapshots");
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  // Server-rendered HTML report/simulation, opened as a blob in a new tab - the console app tried
  // a React-rendered version of both for a while, but the plain server-rendered HTML page won out
  // (faster, no MUI Table overhead at this row/column volume) - see adminsDaikinController.js.
  const openReport = async (ids, anchor) => {
    if (!ids.length) {
      return;
    }
    try {
      const blob = await GlobalAdmin.getUnitMappingSnapshotsReportBlob(ids, filters.address ? { address: filters.address } : {});
      window.open(`${URL.createObjectURL(blob)}${anchor ? `#${anchor}` : ""}`, "_blank");
    } catch (err) {
      setError(err?.message || "Failed to load report");
    }
  };

  const viewFullReport = () => openReport(rows.map((r) => r.id));

  const viewLineReport = (deviceSerial, line) =>
    openReport(
      rows.filter((r) => r.deviceSerial === deviceSerial && r.line === line).map((r) => r.id),
      `line-${deviceSerial}-${line}`
    );

  const viewRoundReport = (row) => openReport([row.id], `snap-${row.id}`);

  const openSimulation = async (ids) => {
    if (!ids.length) {
      return;
    }
    try {
      const blob = await GlobalAdmin.getUnitMappingSyncSimulationBlob(ids);
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (err) {
      setError(err?.message || "Failed to load simulation");
    }
  };

  const simulateAll = () => openSimulation(rows.map((r) => r.id));

  const simulateLine = (deviceSerial, line) =>
    openSimulation(rows.filter((r) => r.deviceSerial === deviceSerial && r.line === line).map((r) => r.id));

  const simulateRound = (row) => openSimulation([row.id]);

  return (
    <Paper elevation={0} sx={{ maxWidth: 1100, border: 1, borderColor: "divider", bgcolor: "background.paper", p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2, alignItems: "center" }}>
        <TextField select size="small" label="Line type" value={filters.lineType} onChange={setFilter("lineType")} sx={{ width: 120 }}>
          <MenuItem value="service">service</MenuItem>
          <MenuItem value="outdoor">outdoor</MenuItem>
        </TextField>
        <TextField size="small" label="Customer (id or name)" value={filters.customer} onChange={setFilter("customer")} sx={{ width: 180 }} />
        <TextField size="small" label="Device serial" value={filters.device} onChange={setFilter("device")} sx={{ width: 150 }} />
        <TextField size="small" label="Line" type="number" value={filters.line} onChange={setFilter("line")} sx={{ width: 90 }} />
        <TextField
          size="small"
          label="Address (report only)"
          type="number"
          value={filters.address}
          onChange={setFilter("address")}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          label="From date"
          type="date"
          value={filters.fromDate}
          onChange={setFilter("fromDate")}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          label="To date"
          type="date"
          value={filters.toDate}
          onChange={setFilter("toDate")}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          label="Limit rows"
          type="number"
          value={filters.limitRows}
          onChange={setFilter("limitRows")}
          sx={{ width: 110 }}
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, alignItems: "center" }}>
        {STATUS_OPTIONS.map((status) => (
          <FormControlLabel
            key={status}
            control={<Checkbox size="small" checked={filters.statuses.includes(status)} onChange={toggleStatus(status)} />}
            label={status}
          />
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, mb: 3, alignItems: "center" }}>
        <Button variant="contained" size="small" startIcon={<PlayArrowIcon />} onClick={runQuery} disabled={loading}>
          {loading ? <CircularProgress size={16} color="inherit" /> : "Process"}
        </Button>
        <Button variant="outlined" size="small" startIcon={<AssessmentIcon />} onClick={viewFullReport} disabled={!rows.length}>
          View full report
        </Button>
        <Button variant="outlined" size="small" startIcon={<BugReportIcon />} onClick={simulateAll} disabled={!rows.length}>
          Simulate all
        </Button>
        {summary && (
          <Typography variant="caption" color="text.secondary">
            {summary.total} snapshots, {summary.customerCount} customers, {summary.deviceCount} devices
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box className={classes.loadingBox}>
          <CircularProgress />
        </Box>
      ) : !rows.length ? (
        <Alert severity="info">{summary ? "No unit mapping snapshots found for these filters." : "Set your filters and press Process."}</Alert>
      ) : (
        <TableContainer>
          <Table size="small" className={classes.tableAuto}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.th}>Time</TableCell>
                <TableCell className={classes.th}>Changes</TableCell>
                <TableCell className={classes.th} sx={{ width: 50 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {[...groupedRows.entries()].map(([customerKey, customer]) => {
                const collapsed = collapsedCustomers.has(customerKey);
                const roundCount = [...customer.devices.values()].reduce(
                  (sum, lines) => sum + [...lines.values()].reduce((s, l) => s + l.rows.length, 0),
                  0
                );
                return (
                  <React.Fragment key={customerKey}>
                    <TableRow sx={{ cursor: "pointer", bgcolor: "action.selected" }} onClick={() => toggleCustomer(customerKey)}>
                      <TableCell colSpan={3} className={classes.td} sx={{ py: "8px" }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); toggleCustomer(customerKey); }}>
                            {collapsed ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{customer.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {roundCount} round(s)
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {!collapsed &&
                      [...customer.devices.entries()].map(([deviceSerial, lines]) => (
                        <React.Fragment key={deviceSerial}>
                          <TableRow>
                            <TableCell colSpan={3} className={classes.tdMono} sx={{ pl: 5, fontWeight: 600, py: "6px" }}>
                              {deviceSerial}
                            </TableCell>
                          </TableRow>

                          {[...lines.values()].map((lineGroup) => {
                            const lineKey = `${deviceSerial}|${lineGroup.line}`;
                            return (
                              <React.Fragment key={lineKey}>
                                <TableRow>
                                  <TableCell colSpan={2} className={classes.td} sx={{ pl: 7, color: "text.secondary", py: "4px" }}>
                                    Line {lineGroup.line} ({lineGroup.lineType}) - {lineGroup.rows.length} round(s)
                                  </TableCell>
                                  <TableCell className={classes.td} sx={{ py: "4px" }}>
                                    <Tooltip title="View all rounds for this line">
                                      <IconButton size="small" onClick={() => viewLineReport(deviceSerial, lineGroup.line)}>
                                        <AssessmentIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Simulate all rounds for this line">
                                      <IconButton size="small" onClick={() => simulateLine(deviceSerial, lineGroup.line)}>
                                        <BugReportIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>

                                {lineGroup.rows.map((row) => (
                                  <TableRow key={row.id} hover>
                                    <TableCell className={classes.td} sx={{ pl: 9 }}>
                                      <Typography variant="caption" display="block">
                                        {fmtDate(row.createdAt)}
                                      </Typography>
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                        {row.id}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {row.diff}
                                      </Typography>
                                    </TableCell>
                                    <TableCell className={classes.td}>
                                      <Typography variant="body2">{row.summary}</Typography>
                                      {row.detailLines.map((line, index) => (
                                        <Typography
                                          key={index}
                                          variant="caption"
                                          display="block"
                                          sx={{ color: "text.secondary", fontFamily: "monospace" }}
                                        >
                                          {line}
                                        </Typography>
                                      ))}
                                    </TableCell>
                                    <TableCell className={classes.td}>
                                      <Tooltip title="View this round in the report">
                                        <IconButton size="small" onClick={() => viewRoundReport(row)}>
                                          <AssessmentIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Simulate this round">
                                        <IconButton size="small" onClick={() => simulateRound(row)}>
                                          <BugReportIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
